/**
 * Otokoç widget kit — CSS injection + runtime DOM overrides.
 *
 * Hides unwanted embedded chat controls (cart, attachments, sort),
 * reskins the desktop launcher as a pill with avatar + label, and applies
 * Otokoç product-card / detail-panel visual polish.
 *
 * Returns a cleanup function that removes the stylesheet and observers.
 */

import { CHAT_APPEARANCE_STYLES, applyChatAppearance } from './chatAppearance.js';
import { queryAllAcrossRoots } from './domUtils.js';
import { OTOKOC_LAUNCHER_AVATAR_URL, OTOKOC_DESKTOP_LAUNCHER_LABEL } from './config.js';

const DESKTOP_CHAT_LAUNCHER_IMAGE_URL = OTOKOC_LAUNCHER_AVATAR_URL;
const SHADOW_STYLE_ID = 'otokoc-widget-shadow-overrides';
const SHADOW_STYLE_TEXT = `
  ${CHAT_APPEARANCE_STYLES}

  [data-gengage-part="chat-header-history"] {
    display: none !important;
  }

  @media (max-width: 768px) {
    .gengage-chat-root.gengage-chat-root--mobile.gengage-chat-root--mobile-full
      .gengage-chat-drawer {
      top: 0 !important;
      bottom: auto !important;
      height: var(--gengage-chat-visual-vh, 100dvh) !important;
      max-height: var(--gengage-chat-visual-vh, 100dvh) !important;
      transform: none !important;
      transition: none !important;
      will-change: auto !important;
    }

    .gengage-chat-root.gengage-chat-root--mobile .gengage-chat-input-area {
      position: relative !important;
      bottom: auto !important;
      transform: none !important;
      transition: none !important;
      will-change: auto !important;
      flex: 0 0 auto !important;
    }

    .gengage-chat-root.gengage-chat-root--mobile .gengage-chat-comparison-dock-slot {
      transform: none !important;
      transition: none !important;
      will-change: auto !important;
    }
  }
`;

export function mountWidgetOverrides(): () => void {
  const styleId = 'otokoc-widget-overrides';
  const existing = document.getElementById(styleId);
  let observer: MutationObserver | null = null;
  let intervalId: number | null = null;
  const shadowRoots = new Set<ShadowRoot>();
  if (existing) {
    return () => {};
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
  ${CHAT_APPEARANCE_STYLES}

    [data-gengage-part="chat-header-cart"],
    [data-gengage-part="chat-header-history"],
    [data-gengage-part="chat-attach-button"],
    [data-gengage-part="chat-attach-menu"],
    [data-gengage-part="chat-attach-menu-select-photo"],
    [data-gengage-part="chat-attach-menu-paste"],
    [data-gengage-part="chat-attachment-preview-remove"],
    .gengage-chat-choice-prompter,
    .gengage-chat-product-details-share,
    .gengage-chat-product-card-img-actions,
    .gengage-chat-find-similar-pill,
    .gengage-chat-favorite-btn {
      display: none !important;
    }

    @media (min-width: 769px) {
      [data-gengage-part="chat-launcher-container"] {
        display: none !important;
        pointer-events: none !important;
        overflow: visible !important;
      }

      [data-gengage-part="chat-launcher-button"].gengage-chat-launcher--image-mode {
        width: 237px !important;
        min-width: 237px !important;
        max-width: 237px !important;
        height: 58px !important;
        padding: 8px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 12px !important;
        border-radius: 999px !important;
        border: 1px solid #f1f5f9 !important;
        background: rgba(255, 255, 255, 0.95) !important;
        box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.22), 0 8px 16px rgba(15, 23, 42, 0.08) !important;
        backdrop-filter: blur(12px) !important;
        -webkit-backdrop-filter: blur(12px) !important;
      }

      [data-gengage-part="chat-launcher-button"].gengage-chat-launcher--image-mode > img {
        width: 40px !important;
        height: 40px !important;
        flex: 0 0 40px !important;
        border-radius: 999px !important;
        object-fit: cover !important;
        object-position: center 38% !important;
        border: 1px solid #f1f5f9 !important;
      }

      .otokoc-launcher-label {
        color: #ffffff !important;
        font: 700 14px/1 "Segoe UI", "Helvetica Neue", Arial, sans-serif !important;
        letter-spacing: -0.025em !important;
        white-space: nowrap !important;
        flex: 1 1 auto !important;
        min-width: 0 !important;
        min-height: 40px !important;
        padding: 8px 16px !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        gap: 8px !important;
        border: none !important;
        border-radius: 999px !important;
        background: #ff8000 !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08) !important;
      }

      .otokoc-launcher-label::before {
        content: "" !important;
        width: 20px !important;
        height: 20px !important;
        flex: 0 0 20px !important;
        background-color: #ffffff !important;
        -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'/%3E%3C/svg%3E") center / contain no-repeat !important;
        mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'/%3E%3C/svg%3E") center / contain no-repeat !important;
      }

      [data-gengage-part="chat-header-avatar"] {
        width: 44px !important;
        height: 44px !important;
        border-radius: 999px !important;
        object-fit: cover !important;
        object-position: center 38% !important;
      }
    }

    .otokoc-product-video-showcase {
      margin-top: 14px;
      border: 1px solid rgba(15, 43, 91, 0.14);
      border-radius: 14px;
      background: linear-gradient(165deg, rgba(15, 43, 91, 0.03), rgba(255, 255, 255, 0.96));
      padding: 12px;
    }

    .otokoc-product-video-showcase-title {
      margin: 0;
      color: #0f2b5b;
      font-size: 14px;
      font-weight: 700;
    }

    .otokoc-product-video-showcase-subtitle {
      margin: 4px 0 10px;
      color: #3c4f6e;
      font-size: 11px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }

    .otokoc-product-video-grid {
      display: grid;
      gap: 8px;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    }

    .otokoc-product-video,
    .otokoc-product-video-frame {
      width: 100%;
      border-radius: 10px;
      border: 1px solid rgba(15, 43, 91, 0.18);
      background: #060d1a;
      aspect-ratio: 16 / 9;
    }

    .gengage-chat-product-details-similars {
      display: block !important;
      width: 100% !important;
    }

    .gengage-chat-product-details-similars .gengage-chat-product-grid-wrapper {
      width: 100% !important;
    }

    body.glov-otokoc-chat-open .glov-voice-chat-container {
      display: none !important;
      visibility: hidden !important;
      pointer-events: none !important;
    }

    @media (max-width: 768px) {
      html.glov-otokoc-chat-open,
      body.glov-otokoc-chat-open {
        overflow: hidden !important;
        overscroll-behavior: none !important;
      }

      body.glov-otokoc-chat-open .sticky-content,
      body.glov-otokoc-chat-open .sticky-whatsapp-link {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
      }
    }
  `;
  document.head.appendChild(style);

  const clearInlineStyle = (element: HTMLElement, property: string) => {
    element.style.removeProperty(property);
  };

  const setImportant = (element: HTMLElement, property: string, value: string) => {
    element.style.setProperty(property, value, 'important');
  };

  const ensureShadowStyles = () => {
    for (const element of queryAllAcrossRoots(
      '.gengage-chat-root, .gengage-chat-drawer, [data-gengage-part="chat-input-area"]'
    )) {
      const root = element.getRootNode();
      if (!(root instanceof ShadowRoot) || shadowRoots.has(root)) {
        continue;
      }

      shadowRoots.add(root);
      if (root.querySelector(`#${SHADOW_STYLE_ID}`)) {
        continue;
      }

      const shadowStyle = document.createElement('style');
      shadowStyle.id = SHADOW_STYLE_ID;
      shadowStyle.textContent = SHADOW_STYLE_TEXT;
      root.appendChild(shadowStyle);
    }
  };

  const applyWidgetUiOverrides = () => {
    const selectors = [
      '[data-gengage-part="chat-header-cart"]',
      '[data-gengage-part="chat-header-history"]',
      '[data-gengage-part="chat-attach-button"]',
      '[data-gengage-part="chat-attach-menu"]',
      '[data-gengage-part="chat-attach-menu-select-photo"]',
      '[data-gengage-part="chat-attach-menu-paste"]',
      '[data-gengage-part="chat-attachment-preview-remove"]',
      '.gengage-chat-choice-prompter',
      '.gengage-chat-product-sort-dropdown',
      '.gengage-chat-product-card-img-actions',
      '.gengage-chat-find-similar-pill',
      '.gengage-chat-favorite-btn',
    ];

    for (const selector of selectors) {
      for (const element of queryAllAcrossRoots(selector)) {
        element.remove();
      }
    }

    for (const actions of queryAllAcrossRoots('.gengage-chat-product-grid-head-actions')) {
      if (actions.childElementCount === 0) {
        actions.remove();
      }
    }

    for (const toolbar of queryAllAcrossRoots('.gengage-chat-product-sort-toolbar')) {
      if (toolbar.childElementCount === 0) {
        toolbar.remove();
      }
    }
  };

  const applyRuntimeSizingOverrides = () => {
    const isDesktop = window.innerWidth >= 769;
    const wideDesktop = window.innerWidth >= 1024;

    for (const container of queryAllAcrossRoots('[data-gengage-part="chat-launcher-container"]')) {
      container.style.setProperty('display', 'none');
      container.style.setProperty('pointer-events', 'none');
      if (isDesktop) {
        container.style.setProperty('overflow', 'visible');
      } else {
        clearInlineStyle(container, 'overflow');
      }
    }

    for (const launcher of queryAllAcrossRoots('[data-gengage-part="chat-launcher-button"]')) {
      if (!launcher.classList.contains('gengage-chat-launcher--image-mode')) {
        continue;
      }

      const launcherImage = launcher.querySelector('img');
      const launcherLabel = launcher.querySelector('.otokoc-launcher-label');

      if (isDesktop) {
        launcher.style.setProperty('width', '237px');
        launcher.style.setProperty('height', '58px');
        launcher.style.setProperty('min-width', '237px');
        launcher.style.setProperty('max-width', '237px');
        launcher.style.setProperty('min-height', '58px');
        launcher.style.setProperty('padding', '8px');
        launcher.style.setProperty('display', 'inline-flex');
        launcher.style.setProperty('align-items', 'center');
        launcher.style.setProperty('justify-content', 'flex-start');
        launcher.style.setProperty('gap', '12px');
        launcher.style.setProperty('border', '1px solid #f1f5f9');
        launcher.style.setProperty('border-radius', '999px');
        launcher.style.setProperty('background', 'rgba(255, 255, 255, 0.95)');
        launcher.style.setProperty(
          'box-shadow',
          '0 25px 50px -12px rgba(15, 23, 42, 0.22), 0 8px 16px rgba(15, 23, 42, 0.08)'
        );
        launcher.style.setProperty('backdrop-filter', 'blur(12px)');
        launcher.style.setProperty('-webkit-backdrop-filter', 'blur(12px)');
        launcher.style.setProperty('overflow', 'visible');
        launcher.setAttribute('aria-label', 'Otokoç AI sohbeti aç');

        if (launcherImage instanceof HTMLImageElement) {
          if (launcherImage.src !== OTOKOC_LAUNCHER_AVATAR_URL) {
            launcherImage.src = OTOKOC_LAUNCHER_AVATAR_URL;
          }
          launcherImage.style.setProperty('width', '40px');
          launcherImage.style.setProperty('height', '40px');
          launcherImage.style.setProperty('flex', '0 0 40px');
          launcherImage.style.setProperty('border-radius', '999px');
          launcherImage.style.setProperty('object-fit', 'cover');
          launcherImage.style.setProperty('object-position', 'center 38%');
          launcherImage.style.setProperty('border', '1px solid #f1f5f9');
        }

        if (!launcherLabel) {
          const label = document.createElement('span');
          label.className = 'otokoc-launcher-label';
          label.textContent = OTOKOC_DESKTOP_LAUNCHER_LABEL;
          label.style.setProperty('color', '#ffffff');
          label.style.setProperty(
            'font',
            '700 14px/1 "Segoe UI", "Helvetica Neue", Arial, sans-serif'
          );
          label.style.setProperty('letter-spacing', '-0.025em');
          label.style.setProperty('white-space', 'nowrap');
          label.style.setProperty('flex', '1 1 auto');
          label.style.setProperty('min-width', '0');
          label.style.setProperty('min-height', '40px');
          label.style.setProperty('padding', '8px 16px');
          label.style.setProperty('display', 'inline-flex');
          label.style.setProperty('align-items', 'center');
          label.style.setProperty('justify-content', 'center');
          label.style.setProperty('gap', '8px');
          label.style.setProperty('border', 'none');
          label.style.setProperty('border-radius', '999px');
          label.style.setProperty('background', '#ff8000');
          label.style.setProperty('box-shadow', '0 1px 2px rgba(15, 23, 42, 0.08)');
          launcher.appendChild(label);
        }
      } else {
        clearInlineStyle(launcher, 'width');
        clearInlineStyle(launcher, 'height');
        clearInlineStyle(launcher, 'min-width');
        clearInlineStyle(launcher, 'max-width');
        clearInlineStyle(launcher, 'min-height');
        clearInlineStyle(launcher, 'display');
        clearInlineStyle(launcher, 'align-items');
        clearInlineStyle(launcher, 'justify-content');
        clearInlineStyle(launcher, 'gap');
        clearInlineStyle(launcher, 'border');
        clearInlineStyle(launcher, 'border-radius');
        clearInlineStyle(launcher, 'padding');
        clearInlineStyle(launcher, 'background');
        clearInlineStyle(launcher, 'box-shadow');
        clearInlineStyle(launcher, 'backdrop-filter');
        clearInlineStyle(launcher, '-webkit-backdrop-filter');
        clearInlineStyle(launcher, 'overflow');

        if (launcherImage instanceof HTMLImageElement) {
          if (launcherImage.src !== DESKTOP_CHAT_LAUNCHER_IMAGE_URL) {
            launcherImage.src = DESKTOP_CHAT_LAUNCHER_IMAGE_URL;
          }
          clearInlineStyle(launcherImage, 'width');
          clearInlineStyle(launcherImage, 'height');
          clearInlineStyle(launcherImage, 'flex');
          clearInlineStyle(launcherImage, 'border-radius');
          clearInlineStyle(launcherImage, 'object-fit');
          clearInlineStyle(launcherImage, 'object-position');
        }

        launcherLabel?.remove();
      }
    }

    for (const launcherImage of queryAllAcrossRoots(
      '[data-gengage-part="chat-launcher-button"].gengage-chat-launcher--image-mode > img'
    )) {
      if (isDesktop) {
        launcherImage.style.setProperty('width', '40px');
        launcherImage.style.setProperty('height', '40px');
        launcherImage.style.setProperty('flex', '0 0 40px');
        launcherImage.style.setProperty('border-radius', '999px');
        launcherImage.style.setProperty('object-fit', 'cover');
        launcherImage.style.setProperty('object-position', 'center 38%');
        launcherImage.style.setProperty('display', 'block');
        launcherImage.style.setProperty('border', '1px solid #f1f5f9');
      } else {
        clearInlineStyle(launcherImage, 'width');
        clearInlineStyle(launcherImage, 'height');
        clearInlineStyle(launcherImage, 'flex');
        clearInlineStyle(launcherImage, 'border-radius');
        clearInlineStyle(launcherImage, 'object-fit');
        clearInlineStyle(launcherImage, 'object-position');
        clearInlineStyle(launcherImage, 'display');
        clearInlineStyle(launcherImage, 'border');
      }
    }

    for (const grid of queryAllAcrossRoots('.gengage-chat-product-grid')) {
      setImportant(
        grid,
        'grid-template-columns',
        wideDesktop ? 'repeat(2, minmax(0, 1fr))' : '1fr'
      );
      setImportant(grid, 'gap', wideDesktop ? '20px' : '14px');
    }

    for (const imageWrap of queryAllAcrossRoots('.gengage-chat-product-card-img-wrapper')) {
      setImportant(imageWrap, 'aspect-ratio', '16 / 9');
      clearInlineStyle(imageWrap, 'height');
      setImportant(imageWrap, 'min-height', '180px');
      setImportant(imageWrap, 'padding', '8px');
      setImportant(imageWrap, 'background', 'linear-gradient(180deg, #f3f6fb 0%, #eef2f8 100%)');
    }

    for (const image of queryAllAcrossRoots('.gengage-chat-product-card-img')) {
      if (!(image instanceof HTMLImageElement)) continue;
      setImportant(image, 'width', '100%');
      setImportant(image, 'height', 'auto');
      setImportant(image, 'max-height', '100%');
      setImportant(image, 'display', 'block');
      setImportant(image, 'object-fit', 'contain');
      setImportant(image, 'object-position', 'center center');
    }

    for (const card of queryAllAcrossRoots('.gengage-chat-product-card')) {
      setImportant(card, 'border-radius', '20px');
      setImportant(card, 'border', '1px solid #d6dde8');
      setImportant(card, 'box-shadow', '0 8px 24px rgba(16, 42, 67, 0.08)');
      setImportant(card, 'overflow', 'hidden');
    }

    for (const cta of queryAllAcrossRoots('.gengage-chat-product-card-cta')) {
      setImportant(cta, 'text-decoration', 'none');
      setImportant(cta, 'font-weight', '800');
      setImportant(cta, 'border-radius', '12px');
    }

    for (const similars of queryAllAcrossRoots('.gengage-chat-product-details-similars')) {
      setImportant(similars, 'display', 'block');
      setImportant(similars, 'width', '100%');
    }

    for (const similarGrid of queryAllAcrossRoots(
      '.gengage-chat-product-details-similars .gengage-chat-product-grid'
    )) {
      setImportant(
        similarGrid,
        'grid-template-columns',
        wideDesktop ? 'repeat(2, minmax(0, 1fr))' : '1fr'
      );
      setImportant(similarGrid, 'gap', wideDesktop ? '18px' : '12px');
      setImportant(similarGrid, 'align-items', 'stretch');
    }

    for (const detailPanel of queryAllAcrossRoots('.gengage-chat-product-details-panel')) {
      setImportant(detailPanel, 'display', 'flex');
      setImportant(detailPanel, 'flex-direction', 'column');
      setImportant(detailPanel, 'grid-template-columns', 'none');
      setImportant(detailPanel, 'align-items', 'stretch');
      setImportant(detailPanel, 'max-width', '1180px');
      setImportant(detailPanel, 'width', '100%');
    }

    for (const detailMedia of queryAllAcrossRoots('.gengage-chat-product-details-media')) {
      setImportant(detailMedia, 'padding', '0');
      setImportant(detailMedia, 'border', '0');
      setImportant(detailMedia, 'background', 'transparent');
      setImportant(detailMedia, 'align-items', 'stretch');
    }

    for (const detailMediaWrap of queryAllAcrossRoots('.gengage-chat-product-details-img-wrap')) {
      setImportant(detailMediaWrap, 'padding', '0');
      setImportant(detailMediaWrap, 'width', '100%');
      setImportant(detailMediaWrap, 'align-self', 'stretch');
      setImportant(detailMediaWrap, 'position', 'relative');
      setImportant(detailMediaWrap, 'top', 'auto');
      setImportant(
        detailMediaWrap,
        'background',
        'linear-gradient(180deg, #f3f6fb 0%, #eef2f8 100%)'
      );
    }

    for (const detailContent of queryAllAcrossRoots('.gengage-chat-product-details-content')) {
      setImportant(detailContent, 'width', '100%');
      setImportant(detailContent, 'padding', window.innerWidth >= 960 ? '18px' : '14px');
      setImportant(detailContent, 'box-sizing', 'border-box');
    }

    for (const detailCtas of queryAllAcrossRoots('[data-otokoc-part="detail-ctas"]')) {
      setImportant(detailCtas, 'width', '100%');
      setImportant(detailCtas, 'box-sizing', 'border-box');
    }

    for (const detailCtaRows of queryAllAcrossRoots('[data-otokoc-part="detail-ctas"] > div')) {
      setImportant(detailCtaRows, 'width', '100%');
      setImportant(detailCtaRows, 'min-width', '0');
    }

    for (const detailCtaButtons of queryAllAcrossRoots('[data-otokoc-part="detail-ctas"] button')) {
      setImportant(detailCtaButtons, 'width', '100%');
      setImportant(detailCtaButtons, 'min-width', '0');
    }

    for (const summaryCard of queryAllAcrossRoots('[data-gengage-part="product-summary-card"]')) {
      setImportant(summaryCard, 'border', '1px solid #d6dde8');
      setImportant(summaryCard, 'border-radius', '16px');
      setImportant(summaryCard, 'box-shadow', '0 6px 18px rgba(16, 42, 67, 0.08)');
      setImportant(summaryCard, 'overflow', 'hidden');
    }

    for (const summaryImageWrap of queryAllAcrossRoots('.gengage-chat-product-summary__image')) {
      setImportant(summaryImageWrap, 'aspect-ratio', '16 / 9');
      setImportant(summaryImageWrap, 'height', 'auto');
      setImportant(summaryImageWrap, 'padding', '8px');
      setImportant(
        summaryImageWrap,
        'background',
        'linear-gradient(180deg, #f3f6fb 0%, #eef2f8 100%)'
      );
    }

    for (const summaryImage of queryAllAcrossRoots('.gengage-chat-product-summary__image img')) {
      if (!(summaryImage instanceof HTMLImageElement)) continue;
      setImportant(summaryImage, 'width', '100%');
      setImportant(summaryImage, 'height', '100%');
      setImportant(summaryImage, 'object-fit', 'contain');
      setImportant(summaryImage, 'object-position', 'center center');
    }

    for (const summaryCta of queryAllAcrossRoots('.gengage-chat-product-summary__cta')) {
      if (!(summaryCta instanceof HTMLAnchorElement)) continue;

      setImportant(summaryCta, 'text-decoration', 'none');
      setImportant(summaryCta, 'font-weight', '800');
      setImportant(summaryCta, 'border', '1px solid rgba(15, 43, 91, 0.2)');
      setImportant(summaryCta, 'background', '#f6f9ff');
      setImportant(summaryCta, 'color', '#0f2b5b');
      setImportant(summaryCta, 'border-radius', '10px');
      setImportant(summaryCta, 'padding', '6px 10px');

      if (summaryCta.dataset.otokocBoundClick === '1') continue;
      summaryCta.dataset.otokocBoundClick = '1';
      summaryCta.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const parentCard = summaryCta.closest('[data-gengage-part="product-summary-card"]');
        if (parentCard instanceof HTMLElement) {
          parentCard.dispatchEvent(
            new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
          );
        }
      });
    }

    for (const image of queryAllAcrossRoots('.gengage-chat-product-details-img')) {
      if (!(image instanceof HTMLImageElement)) continue;
      image.style.objectFit = 'contain';
      image.style.objectPosition = 'center center';
      image.style.maxHeight = window.innerWidth >= 960 ? '640px' : '420px';
      image.style.width = '100%';
      image.style.borderRadius = '0';
    }

    for (const headerAvatar of queryAllAcrossRoots('[data-gengage-part="chat-header-avatar"]')) {
      if (!(headerAvatar instanceof HTMLImageElement)) {
        continue;
      }

      if (headerAvatar.src !== OTOKOC_LAUNCHER_AVATAR_URL) {
        headerAvatar.src = OTOKOC_LAUNCHER_AVATAR_URL;
      }

      headerAvatar.classList.remove('gengage-chat-header-avatar--logo');
      headerAvatar.style.setProperty('width', '44px');
      headerAvatar.style.setProperty('height', '44px');
      headerAvatar.style.setProperty('border-radius', '999px');
      headerAvatar.style.setProperty('object-fit', 'cover');
      headerAvatar.style.setProperty('object-position', 'center 38%');
    }
  };

  const applyAllOverrides = () => {
    ensureShadowStyles();
    applyWidgetUiOverrides();
    applyChatAppearance();
    applyRuntimeSizingOverrides();
  };

  applyAllOverrides();
  observer = new MutationObserver(() => {
    applyAllOverrides();
  });
  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
  intervalId = window.setInterval(applyAllOverrides, 450);
  window.addEventListener('resize', applyRuntimeSizingOverrides);

  return () => {
    observer?.disconnect();
    if (intervalId !== null) {
      window.clearInterval(intervalId);
    }
    window.removeEventListener('resize', applyRuntimeSizingOverrides);
    for (const root of shadowRoots) {
      root.querySelector(`#${SHADOW_STYLE_ID}`)?.remove();
    }
    style.remove();
  };
}
